import React, { useEffect, useState } from "react";

// --------------------
// CSV EXPORT
// --------------------
const exportCSV = (rows, filename = "orders.csv") => {
  const header = ["Order ID", "Item", "Status"];

  const csv = [
    header.join(","),
    ...rows.map((r) =>
      [r.id, `"${r.item.replace(/"/g, '""')}"`, r.status].join(",")
    ),
  ].join("\r\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
};

// --------------------
// SHOPIFY API
// --------------------
const fetchOrders = async () => {
  try {
    const res = await fetch(
      "https://s2r1y0wb6j.execute-api.us-west-1.amazonaws.com/Prod/orders"
    );

    if (!res.ok) {
      throw new Error("Orders fetch failed");
    }

    return res.json();
  } catch (err) {
    console.error("Orders error:", err);
    return [];
  }
};

// --------------------
// DIRTY ORDERS API
// --------------------
const fetchDirtyOrders = async () => {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);

    const start = startDate.toISOString().split("T")[0];

    const res = await fetch(
      `https://46gzkdp5vf.execute-api.us-west-1.amazonaws.com/default/shopify-dirty-orders?start=${start}`
    );

    if (!res.ok) {
      throw new Error("Dirty orders fetch failed");
    }

    return await res.json();
  } catch (err) {
    console.error("Dirty orders error:", err);

    return {
      orders: [],
    };
  }
};

// --------------------
// HELPERS
// --------------------
const getColor = (order) => {
  if (order.ready_for_pickup) {
    return "#f4a460";
  }

  if (
    order.in_progress ||
    (order.fulfillment_status || "").toLowerCase() === "partial"
  ) {
    return "#60a5fa";
  }

  const age = Math.floor(
    (new Date() - new Date(order.created_at)) /
      (1000 * 60 * 60 * 24)
  );

  const pct = Math.min(Math.max(age / 30, 0), 1);

  const r = pct < 0.5 ? Math.round(255 * pct * 2) : 255;

  const g =
    pct < 0.5
      ? 255
      : Math.round(255 * (1 - (pct - 0.5) * 2));

  return `rgb(${r},${g},0)`;
};

const formatStatus = (status) =>
  (status || "unfulfilled")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// --------------------
// MAIN COMPONENT
// --------------------
const OrderAgingDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAllItemsOrder, setShowAllItemsOrder] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [showPickupOrders, setShowPickupOrders] = useState(false);
  const [showChampionOrders, setShowChampionOrders] = useState(false);

  // --------------------
  // LOAD DATA
  // --------------------
  const loadOrders = async () => {
    const [ordersData, dirtyData] = await Promise.all([
      fetchOrders(),
      fetchDirtyOrders(),
    ]);

    const now = new Date();

    const dirtyMap = new Map(
      (dirtyData.orders || []).map((o) => [
        o.name.replace(/^#/, ""),
        o,
      ])
    );

    const enriched = ordersData.map((o) => {
      const ageHours = Math.floor(
        (now - new Date(o.created_at)) /
          (1000 * 60 * 60)
      );

      const cleanName = o.name.replace(/^#/, "");
      const dirty = dirtyMap.get(cleanName);

      return {
        ...o,
        name: cleanName,
        ageLabel:
          ageHours < 24
            ? `${ageHours}H`
            : `${Math.floor(ageHours / 24)}D`,
        ageValue: ageHours,
        combineShipping: o.combine_shipping === true,
        is_dirty: !!dirty,
        dirty_details: dirty || null,
      };
    });

    setOrders(enriched);
    setLastUpdated(now);
  };

  useEffect(() => {
    loadOrders();

    const interval = setInterval(loadOrders, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // --------------------
  // CSV DATA
  // --------------------
  const buildCSVData = () => {
    const rows = [];

    orders.forEach((o) =>
      o.line_items.forEach((li) => {
        rows.push({
          id: o.name,
          item: li.sku || li.title,
          status:
            (li.unfulfilled_qty ?? 0) > 0
              ? "Unfulfilled"
              : "Fulfilled",
        });
      })
    );

    return rows;
  };

  // --------------------
  // FILTERS
  // --------------------
 const pickupCount = orders.filter(
  (o) => o.ready_for_pickup
).length;

const championCount = orders.filter(
  (o) => o.champion_schools_delivery
).length;

const visibleOrders = orders.filter((o) => {
  if (
    o.ready_for_pickup &&
    !showPickupOrders
  ) {
    return false;
  }

  if (
    o.champion_schools_delivery &&
    !showChampionOrders
  ) {
    return false;
  }

  return true;
});

  const over24 = visibleOrders.filter((o) => o.ageValue >= 24);
const under24 = visibleOrders.filter((o) => o.ageValue < 24);

const openOrderCount = orders.length;

const readyForPickupCount = orders.filter(
  (o) => o.ready_for_pickup
).length;

const shippingOrderCount = orders.filter(
  (o) =>
    !o.ready_for_pickup &&
    !o.champion_schools_delivery
).length;

const combineShippingCount = orders.filter(
  (o) => o.combineShipping
).length;

const over24Count = orders.filter(
  (o) => o.ageValue >= 24
).length;

const readyToFulfillCount = orders.filter((o) => {
  const items = o.line_items || [];

  return items.every(
    (li) =>
      (li.on_hand ?? 0) >=
      (li.unfulfilled_qty ?? 0)
  );
}).length;

const combineGroups = [
  ...new Map(
    orders
      .filter(
        (o) =>
          o.combineShipping &&
          o.combine_orders?.length > 1
      )
      .map((o) => [
        [...o.combine_orders]
          .sort()
          .join("|"),
        o.combine_orders,
      ])
  ).values(),
];

  // --------------------
  // GRID
  // --------------------
  const renderGrid = (data) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(6,1fr)",
        gap: 10,
        padding: "1rem",
      }}
    >
      {data.map((o) => {
        const isReadyForPickup = o.ready_for_pickup;
        const isChampion =
  o.champion_schools_delivery;

        const isPartial =
          o.in_progress ||
          (o.fulfillment_status || "").toLowerCase() === "partial";

        return (
          <div
            key={o.name}
            onClick={() => setSelectedOrder(o.name)}
            style={{
              backgroundColor: o.is_dirty ? "#fde68a" : getColor(o),
              padding: 10,
              textAlign: "center",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: "bold",
              color: "#000",
            }}
          >
            <div>
              #{o.name}
              {o.combineShipping && " ❗"}
              {isReadyForPickup && " 📦"}
              {isChampion && " 🏫"}
              {!isReadyForPickup && isPartial && " 📭"}
            </div>

            <div style={{ marginTop: 4 }}>{o.ageLabel}</div>
          </div>
        );
      })}
    </div>
  );

  // --------------------
  // DETAILS
  // --------------------
  const renderDetails = (list) => (
    <div>
      {list.map((o) => {
        if (selectedOrder && selectedOrder !== o.name) {
          return null;
        }

        const displayAll = showAllItemsOrder === o.name;

        const items = displayAll
          ? o.line_items
          : o.line_items.filter(
              (li) => (li.unfulfilled_qty ?? 0) > 0
            );

        const allReady = items.every(
          (li) => li.on_hand >= (li.unfulfilled_qty ?? 0)
        );

        const isReadyForPickup = o.ready_for_pickup;
        const isChampion =
  o.champion_schools_delivery;

        const isPartial =
          o.in_progress ||
          (o.fulfillment_status || "").toLowerCase() === "partial";

        return (
          <div key={o.name} style={{ marginBottom: 20 }}>
            <strong
              style={{ cursor: "pointer" }}
              onClick={() =>
                setShowAllItemsOrder((s) =>
                  s === o.name ? null : o.name
                )
              }
            >
              #{o.name}
            </strong>{" "}
            — {formatStatus(o.fulfillment_status)} — {o.ageLabel}

            {allReady && (
              <span
                style={{
                  color: "green",
                  marginLeft: 12,
                  fontWeight: "bold",
                }}
              >
                ✅ READY TO FULFILL
              </span>
            )}

            {isReadyForPickup && (
              <span
                style={{
                  color: "#92400e",
                  marginLeft: 12,
                }}
              >
                📦 Ready for Pickup
              </span>
            )}

{isChampion && (
  <span
    style={{
      color: "#7c3aed",
      marginLeft: 12,
    }}
  >
    🏫 Champion Schools Pickup
  </span>
)}

            {!isReadyForPickup && isPartial && (
              <span
                style={{
                  color: "#2563eb",
                  marginLeft: 12,
                }}
              >
                📭 Partial Fulfillment
              </span>
            )}

            {o.combineShipping && (
              <span
                style={{
                  color: "red",
                  marginLeft: 12,
                }}
              >
                ❗ Combine Shipping
              </span>
            )}

{o.combineShipping &&
  o.combine_orders?.length > 1 && (
    <div
      style={{
        marginTop: 8,
        color: "red",
        fontWeight: "bold",
      }}
    >
      Ship these together:{" "}
      {o.combine_orders
        .filter(
          (orderNo) => orderNo !== o.name
        )
        .map((name) =>
          name.replace(/^#/, "")
        )
        .join(" / ")}
    </div>
)}

            {o.is_dirty && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  background: "#fff3cd",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <strong>
                  ⚠️ Exchange Order (Reimport to Cin7)
                </strong>

                <div style={{ marginTop: 6 }}>
                  <strong>Removed:</strong>{" "}
                  {o.dirty_details?.removed?.join(", ") || "None"}
                </div>

                <div>
                  <strong>Added:</strong>{" "}
                  {o.dirty_details?.added?.join(", ") || "None"}
                </div>
              </div>
            )}

            <ul>
              {items.map((li, i) => (
                <li key={i}>
                  <strong>{li.sku || "MISSING"}</strong> —{" "}
                  {li.title} — {li.unfulfilled_qty ?? 0}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );

  // --------------------
  // UI
  // --------------------
  return (
    <div
  style={{
    padding: "2rem",
    fontFamily: "sans-serif",
    background: "#ffffff",
    minHeight: "100vh",
  }}
>
     <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  }}
>
  <h1 style={{ margin: 0 }}>
    Order Aging Dashboard
  </h1>

  <div
    style={{
      display: "flex",
      gap: "10px",
    }}
  >
    <button
      onClick={() =>
        exportCSV(
          buildCSVData(),
          "order-unfulfilled-items.csv"
        )
      }
    >
      Export CSV
    </button>

    <button
      onClick={() =>
        setShowPickupOrders(!showPickupOrders)
      }
    >
      {showPickupOrders
        ? "Hide Pickup"
        : `Show Pickup (${pickupCount})`}
    </button>
      <button
  onClick={() =>
    setShowChampionOrders(
      !showChampionOrders
    )
  }
>
  {showChampionOrders
    ? "Hide Champion"
    : `Show Champion (${championCount})`}
</button>
  
  </div>
</div>

<div
  style={{
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  }}
>
  {[
  {
    label: "Open Orders",
    value: openOrderCount,
    icon: "📋",
  },
  {
    label: "Pickup",
    value: readyForPickupCount,
    icon: "📦",
  },
  {
    label: "Champion",
    value: championCount,
    icon: "🏫",
  },
  {
    label: "Shipping",
    value: shippingOrderCount,
    icon: "🚚",
  },
  {
    label: "Over 24h",
    value: over24Count,
    icon: "⏰",
  },
  {
    label: "Combine",
    value: combineShippingCount,
    icon: "❗",
  },
].map((card) => (
    <div
      key={card.label}
      style={{
        background: "#fff",
        borderRadius: "10px",
        padding: "16px",
        boxShadow:
          "0 1px 3px rgba(0,0,0,0.1)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          color: "#6b7280",
        }}
      >
        {card.icon} {card.label}
      </div>

      <div
        style={{
          fontSize: "32px",
          fontWeight: "bold",
          marginTop: "8px",
        }}
      >
        {card.value}
      </div>
    </div>
  ))}
</div>

{combineGroups.length > 0 && (
  <div
    style={{
      background: "#fff7ed",
      border: "1px solid #fb923c",
      borderRadius: "10px",
      padding: "12px",
      marginBottom: "16px",
    }}
  >
    <strong>
      ❗ Combine Shipping Groups
    </strong>

    <div
      style={{
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
        marginTop: "10px",
      }}
    >
      {combineGroups.map((group, idx) => (
        <div
          key={idx}
          style={{
            background: "white",
            border: "1px solid #fdba74",
            borderRadius: "999px",
            padding: "8px 12px",
            fontWeight: "bold",
          }}
        >
          {group
            .map((o) => o.replace(/^#/, ""))
            .join(" + ")}
        </div>
      ))}
    </div>
  </div>
)}

<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    fontSize: "14px",
    flexWrap: "wrap",
  }}
>
  <div>
    Updated: {lastUpdated.toLocaleTimeString()}
  </div>

  <div>
    📭 Partial | 📦 Pickup | 🏫 Champion | ❗ Combine
  </div>

  {(!showPickupOrders ||
  !showChampionOrders) && (
      <div
        style={{
          color: "#92400e",
          fontWeight: "bold",
        }}
      >
        <>
  {!showPickupOrders &&
    pickupCount > 0 &&
    `${pickupCount} Pickup Hidden `}

  {!showChampionOrders &&
    championCount > 0 &&
    `${championCount} Champion Hidden`}
</>
      </div>
    )}
</div>

      {selectedOrder && (
        <p>
          <button onClick={() => setSelectedOrder(null)}>
            ⭠ Show All Orders
          </button>
        </p>
      )}

      <div
        style={{
          display: "flex",
          gap: "2rem",
        }}
      >
        <div style={{ flex: 1 }}>
          <h2>Orders Over 24 Hours</h2>

          {renderGrid(over24)}

          {renderDetails(over24)}
        </div>

        <div style={{ flex: 1 }}>
          <h2>New Orders</h2>

          {renderGrid(under24)}

          {renderDetails(under24)}
        </div>
      </div>
    </div>
  );
};

export default OrderAgingDashboard;
